import { classNames } from 'shared/lib/classNames/classNames'
import cls from './FriendsPage.module.scss'
import { Button } from 'shared/ui/Button'
import {
    Text,
    TextAlign,
    TextColor,
    TextSize,
    TextWeight,
} from 'shared/ui/Text/Text'
import { useDispatch, useSelector } from 'react-redux'
import whaleThumbsUp from 'shared/assets/images/whaleThumbsUp.png'
import { FriendsItem } from 'widgets/FriendsItem/ui/FriendsItem'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { navActions } from 'entities/NavMenu'
import { useTransition } from '@react-spring/web'
import { ClearIcon, CloseIcon, SearchIcon, ShareIcon } from 'shared/ui/Icon'
import { Loader } from 'shared/ui/Loader'
import { Popup } from 'shared/ui/Popup/Popup'
import { copyTextToClipboard } from 'shared/lib/copyTextToTextClipboard/copyTextToTextClipboard'
import { copyApplicationLink } from 'shared/lib/copyApplicationLink/copyApplicationLink'
import { Nick } from './Nick/Nick'
import { fetchFriendProfiles } from 'shared/api/fetchFriendProfiles'
import { searchProfileData } from 'shared/api/searchProfileData'
import { fetchTopCreators } from 'shared/api/fetchTopCreators'
import React from 'react'
import {
    acknowledgeCreatedCount,
    getCreatedCountStorageKey,
    getLocalDateKey,
    getPendingCreatedCountIds,
    readCreatedCountSnapshots,
    reconcileCreatedCountSnapshots,
    writeCreatedCountSnapshots,
} from './model/createdCountUpdates'

const TOP_CREATORS_LIMIT = 20

const FriendsPage = () => {
    const dispatch = useDispatch()
    const friends = useSelector((state) => state.auth.friends)
    const id = useSelector((state) => state.auth.id)
    const nickname = useSelector((state) => state.auth.nickname)

    const allCards = useSelector((state) => state.auth.allCards)

    const [loading, setLoading] = useState(true)
    const [loadingFetchResult, setLoadingFetchResult] = useState(false)

    const [searchValidate, setSearchValidate] = useState('')
    const [nicknameForm, setNicknameForm] = useState('')
    const [nicknameErrorText, setNicknameErrorText] = useState(``)
    const [nicknameChecking, setNicknameChecking] = useState(false)
    const [searchResult, setSearchResult] = useState([])
    const [friendsFetchResult, setFriendsFetchResult] = useState([])
    const [topCreators, setTopCreators] = useState([])
    const [updatedCreatorIds, setUpdatedCreatorIds] = useState(new Set())

    const [showCircle, setShowCircle] = useState(false)

    const [visible, setVisible] = useState(false) // стейт кита копировальщика
    const [popupText, setPopupText] = useState('Ник скопирован')

    const timeoutWhaleCopyRef = useRef(null) // реф для таймаута говорящего кита
    const onSpeak = useCallback((text = 'Ник скопирован') => {
        // ф-ция говорящего кита
        clearTimeout(timeoutWhaleCopyRef.current)
        timeoutWhaleCopyRef.current = null
        setPopupText(text)
        setVisible(true)
        timeoutWhaleCopyRef.current = setTimeout(() => {
            setVisible(false)
        }, 1000)
    }, [])

    const copyNick = (text) => {
        copyTextToClipboard(text)
        onSpeak()
    }

    const copyAppLink = () => {
        onSpeak(copyApplicationLink())
    }

    useEffect(() => {
        let cancelled = false
        setLoading(true)

        fetchFriendProfiles(friends)
            .then((profiles) => {
                if (!cancelled) {
                    setFriendsFetchResult(profiles)
                }
            })
            .catch((error) => {
                console.error('Failed to load friend profiles', error)
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [friends])

    useEffect(() => {
        let cancelled = false

        fetchTopCreators()
            .then((profiles) => {
                if (!cancelled) setTopCreators(profiles)
            })
            .catch((error) => {
                console.error('Failed to load top creators', error)
            })

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        dispatch(navActions.setVisible(true))
    }, [dispatch])

    const transitions = useTransition(
        searchResult.length > 0 ? searchResult : friendsFetchResult,
        {
            //анимация
            from: { opacity: 0, transform: 'translate3d(0,40px,0)' },
            enter: { opacity: 1, transform: 'translate3d(0,0px,0)' },
            config: { tension: 320, friction: 20 },
            trail: 100,
        }
    )

    const nicknameMods = {
        [cls.error]: searchValidate === 'error',
        [cls.onFocusSearch]: nicknameChecking,
    }

    const onChangeNickname = (e) => {
        setNicknameForm(e.target.value.toLowerCase())
    }

    const onMatchNickname = async () => {
        setLoadingFetchResult(true)
        setNicknameErrorText(``)
        setSearchValidate('')
        setSearchResult([])
        if (nicknameChecking && nicknameForm.length > 0) {
            const pattern = /^[a-z0-9_@]+$/

            if (pattern.test(nicknameForm)) {
                const input = nicknameForm.replace(/@/, '')

                if (nicknameForm.length > 30) {
                    setSearchValidate('error')
                    setNicknameErrorText('Максимальная длина - 30 символов')
                    setLoadingFetchResult(false)
                    setShowCircle(true)
                } else {
                    setSearchValidate('validate')
                    try {
                        const profiles = await searchProfileData(input)
                        const result = profiles
                        setSearchResult(result)
                        if (result.length === 0) {
                            setNicknameErrorText(
                                'Никого не нашлось... Проверь, правильно ли введён ник'
                            )
                            setSearchValidate('error')
                        }
                    } catch (error) {
                        console.error('Failed to search profiles', error)
                        setNicknameErrorText(
                            'Не удалось выполнить поиск. Попробуй ещё раз'
                        )
                        setSearchValidate('error')
                    } finally {
                        setLoadingFetchResult(false)
                        setShowCircle(true)
                    }
                }
            } else {
                setSearchValidate('error')
                setNicknameErrorText('Поиск понимает только A–Z, 0–9 и _')
                setLoadingFetchResult(false)
                setShowCircle(true)
            }
        }
    }

    const errorMods = {
        [cls.errorIconLeft]: nicknameChecking,
        [cls.errorIconLeftValidate]:
            nicknameChecking && nicknameErrorText.length === 0,
    }

    const onCloseSearch = () => {
        setNicknameChecking(false)
        setNicknameErrorText(``)
        setNicknameForm('')
        setSearchValidate('')
        setSearchResult([])
        setShowCircle(false)
    }

    const onClearForm = () => {
        setNicknameForm('')
        setNicknameErrorText(``)
        setSearchValidate('')
        setShowCircle(false)
    }

    const createdCounts = useMemo(
        () =>
            allCards.reduce((counts, card) => {
                if (card.userId !== undefined && card.userId !== null) {
                    const creatorId = String(card.userId)
                    counts[creatorId] = (counts[creatorId] || 0) + 1
                }
                return counts
            }, {}),
        [allCards]
    )

    const getCreatedCount = (profile) =>
        profile.createdCount ?? createdCounts[String(profile.id)] ?? 0

    const suggestedCreators = topCreators
        .filter(
            (profile) =>
                String(profile.id) !== String(id) &&
                !friends.some(
                    (friendId) => String(friendId) === String(profile.id)
                )
        )
        .slice(0, TOP_CREATORS_LIMIT)

    const visibleProfiles = useMemo(() => {
        const profiles = [
            ...friendsFetchResult,
            ...topCreators,
            ...searchResult,
        ]
        const uniqueProfiles = new Map()

        profiles.forEach((profile) => {
            if (profile.id === undefined || profile.id === null) return
            uniqueProfiles.set(String(profile.id), {
                id: profile.id,
                createdCount:
                    profile.createdCount ??
                    createdCounts[String(profile.id)] ??
                    0,
            })
        })

        return [...uniqueProfiles.values()]
    }, [createdCounts, friendsFetchResult, searchResult, topCreators])

    useEffect(() => {
        const storageKey = getCreatedCountStorageKey(id)
        if (!storageKey || visibleProfiles.length === 0) {
            setUpdatedCreatorIds(new Set())
            return
        }

        const snapshots = readCreatedCountSnapshots(
            window.localStorage,
            storageKey
        )
        const nextSnapshots = reconcileCreatedCountSnapshots(
            snapshots,
            visibleProfiles,
            getLocalDateKey()
        )

        writeCreatedCountSnapshots(
            window.localStorage,
            storageKey,
            nextSnapshots
        )
        setUpdatedCreatorIds(getPendingCreatedCountIds(nextSnapshots))
    }, [id, visibleProfiles])

    const acknowledgeProfileCreatedCount = (profileId, createdCount) => {
        const storageKey = getCreatedCountStorageKey(id)
        if (!storageKey) return

        const snapshots = readCreatedCountSnapshots(
            window.localStorage,
            storageKey
        )
        const nextSnapshots = acknowledgeCreatedCount(
            snapshots,
            profileId,
            createdCount,
            getLocalDateKey()
        )

        writeCreatedCountSnapshots(
            window.localStorage,
            storageKey,
            nextSnapshots
        )
        setUpdatedCreatorIds(getPendingCreatedCountIds(nextSnapshots))
    }

    const profileItem = (item, style) => {
        const createdCount = getCreatedCount(item)

        return (
            <FriendsItem
                key={item.id}
                style={style}
                id={item.id}
                avatar={item.avatar}
                name={item.name}
                nickname={item.nickname}
                createdCount={createdCount}
                createdCountUpdated={updatedCreatorIds.has(String(item.id))}
                onOpen={() =>
                    acknowledgeProfileCreatedCount(item.id, createdCount)
                }
            />
        )
    }

    return (
        <div
            className={cls.FriendsPage}
            onScroll={() => {
                document.activeElement.blur()
            }}
        >
            <Popup visible={visible} text={popupText} />
            <div className={cls.Container}>
                <Text
                    weight={TextWeight.BOLD}
                    size={TextSize.L}
                    className={cls.Header}
                >
                    Мои друзья
                </Text>
            </div>
            <div className={cls.Search}>
                <div className={cls.SearchContainer}>
                    <SearchIcon
                        className={cls.magnifier}
                        color="var(--ter-black-color)"
                    />
                    <input
                        className={classNames(
                            cls.inputSearch,
                            nicknameMods,
                            []
                        )}
                        style={{ paddingLeft: '50px' }}
                        type="text"
                        onChange={onChangeNickname}
                        onFocus={() => {
                            setNicknameErrorText(``)
                            setSearchValidate('')
                            setNicknameChecking(true)
                        }}
                        onBlur={() => {
                            if (nicknameForm.length === 0) {
                                onCloseSearch()
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                onMatchNickname()
                                document.activeElement.blur()
                            }
                        }}
                        enterKeyHint="search"
                        value={nicknameForm}
                        placeholder={'Поиск по нику'}
                    />
                    {loadingFetchResult &&
                    nicknameForm.length &&
                    nicknameErrorText.length === 0 ? (
                        <Loader
                            style={{ right: '96px', top: '18px' }}
                            size={'small'}
                        />
                    ) : null}
                    {nicknameChecking ? (
                        <Button
                            className={cls.CrossButton}
                            onClick={onCloseSearch}
                        >
                            <CloseIcon />
                        </Button>
                    ) : null}
                </div>
                {showCircle ? (
                    <ClearIcon
                        onClick={onClearForm}
                        className={classNames(cls.errorIcon, errorMods, [])}
                    />
                ) : null}
                {nicknameErrorText.length > 0 ? null : (
                    <Nick
                        nickname={nickname}
                        copyNick={copyNick}
                        id={id}
                        searchValidate={searchValidate}
                    />
                )}
                {nicknameErrorText.length > 0 ? (
                    <Text
                        className={cls.nicknameErrorText}
                        color={
                            searchValidate === 'error'
                                ? TextColor.RED
                                : TextColor.TERBLACK
                        }
                        size={TextSize.XS}
                        align={TextAlign.LEFT}
                    >
                        {nicknameErrorText}
                    </Text>
                ) : null}
            </div>
            {!nicknameChecking ? (
                <>
                    {friends.length > 0 ? (
                        <div className={cls.FriendsContainer}>
                            {!loading ? (
                                <div className={cls.FriendsList}>
                                    {transitions((style, item) => {
                                        return profileItem(item, style)
                                    })}
                                </div>
                            ) : (
                                <div className={cls.ListLoader}>
                                    <Loader />
                                </div>
                            )}
                            <Button
                                className={cls.ButtonInviteFriends}
                                onClick={copyAppLink}
                            >
                                <ShareIcon />
                                <Text
                                    className={cls.ButtonText}
                                    weight={TextWeight.SBOLD}
                                >
                                    Пригласить друзей
                                </Text>
                            </Button>
                        </div>
                    ) : (
                        <div className={cls.EmptyFriends}>
                            <img
                                src={whaleThumbsUp}
                                alt="empty"
                                className={cls.EmptyImage}
                            />
                            <Text
                                className={cls.EmptyText}
                                size={TextSize.XL}
                                weight={TextWeight.BOLD}
                            >
                                С друзьями веселей!
                            </Text>
                            <Text className={cls.EmptySecondText}>
                                Узнай ник друга и добавь его в MineCards
                            </Text>
                            <Button
                                className={cls.Button}
                                onClick={copyAppLink}
                            >
                                <ShareIcon
                                    style={{
                                        width: '24px',
                                        marginLeft: '19px',
                                    }}
                                />
                                <Text
                                    className={cls.ButtonText}
                                    weight={TextWeight.SBOLD}
                                >
                                    Пригласить друзей
                                </Text>
                            </Button>
                        </div>
                    )}
                    {suggestedCreators.length > 0 ? (
                        <section className={cls.TopCreators}>
                            <Text
                                className={cls.TopCreatorsTitle}
                                size={TextSize.S}
                                weight={TextWeight.SBOLD}
                                color={TextColor.TERBLACK}
                                align={TextAlign.LEFT}
                            >
                                Топ 20 создателей мечт:
                            </Text>
                            <div className={cls.FriendsList}>
                                {suggestedCreators.map((creator) => (
                                    <React.Fragment key={creator.id}>
                                        {profileItem(creator)}
                                    </React.Fragment>
                                ))}
                            </div>
                        </section>
                    ) : null}
                </>
            ) : (
                <div className={cls.FriendsContainer}>
                    {searchResult.length > 0 ? (
                        <>
                            {transitions((style, item) => {
                                return profileItem(item, style)
                            })}
                        </>
                    ) : null}
                </div>
            )}
        </div>
    )
}

export default FriendsPage
